import { Component, OnInit, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Subscription } from 'rxjs';
import { MatIconModule } from '@angular/material/icon';
import { MatCardModule } from '@angular/material/card';
import { MatChipsModule } from '@angular/material/chips';
import { MatListModule } from '@angular/material/list';
import { MatButtonModule } from '@angular/material/button';
import { GenericTemplateCommonService } from '../../components/template-ai-common/services/generic-template-common.service';
// import { TableComponent } from '../../components/table/table.component'; // with Consumer Insights Summary table (hidden in template)
import { AreaChartComponent } from '../../charts/area-chart/area-chart.component';
// import { BarLineComboChartComponent } from '../../charts/bar-line-combo-chart/bar-line-combo-chart.component';
import { PieChartComponent } from '../../charts/pie-chart/pie-chart.component';
// import { CitationCardComponent } from '../../components/citation-card/citation-card.component';

@Component({
  selector: 'app-generic-consumer-insights',
  standalone: true,
  imports: [
    CommonModule,
    MatIconModule,
    MatCardModule,
    MatChipsModule,
    MatListModule,
    MatButtonModule,
    // TableComponent,
    AreaChartComponent,
    PieChartComponent,
    // CitationCardComponent
  ],
  templateUrl: './generic-consumer-insights.component.html',
  styleUrl: './generic-consumer-insights.component.scss'
})
export class GenericConsumerInsightsComponent implements OnInit, OnDestroy {
  // Core data properties
  consumerInsightsResponse: any = null;
  data: any = null;

  // Loading and error state

  // Chart data
  satisfactionRadarData: any[] = [];
  decisionFactorsChartData: any[] = [];

  // Table data
  // combinedTableData: any[] = [];
  // combinedTableColumns: any[] = [
  //   { key: 'brand', label: 'Brand', type: 'text' },
  //   { key: 'customer_acquisition_rate', label: 'Customer Acquisition Rate', type: 'percentage' },
  //   { key: 'customer_satisfaction', label: 'Customer Satisfaction', type: 'percentage' },
  //   { key: 'brand_loyalty', label: 'Brand Loyalty', type: 'percentage' },
  //   // { key: 'average_purchase_value', label: 'Avg Purchase Value', type: 'currency' }, // Hidden per requirements
  //   { key: 'recommendation_score', label: 'Recommendation Score (NPS)', type: 'percentage' },
  //   { key: 'market_perception', label: 'Market Perception', type: 'percentage' },
  //   { key: 'retention_rate', label: 'Retention Rate', type: 'percentage' },
  //   { key: 'customer_satisfaction', label: 'Customer Satisfaction', type: 'percentage' }
  // ];

  // Consumer Behavior data
  consumerBehaviorData: any = null;
  purchaseAnalytics: any = null;
  customerPreferences: any = null;
  purchaseFunnel: any = null;

  // Comparative insights
  comparativeInsights: any = null;

  // Evidence sources and recommendations
  evidenceSources: any[] = [];
  recommendations: any[] = [];

  // Demographics data
  demographicsData: any = null;
  genderChartData: any[] = [];
  ageChartData: any[] = [];
  interestChartData: any[] = [];
  userTypesChartData: any[] = [];
  additionalDemographics: any[] = [];
  
  // Chart options for demographics (using bar chart)
  demographicsChartView: [number, number] = [600, 400];

  // Subscriptions
  private subscriptions: Subscription[] = [];

  constructor(
    private templateCommonService: GenericTemplateCommonService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    // Subscribe to consumer insights response
    const analysisSub = this.templateCommonService.consumerInsightsResponse$.subscribe((response: any) => {
      if (response) {
        this.consumerInsightsResponse = response;
        this.data = response.structured_data ?? response;
        this.processData();
        this.cdr.detectChanges();
      }
    });

    // Subscribe to filterData changes to detect competitor additions/removals
    let previousEntityNames: string[] = [];
    // Initialize from current filterData if available
    const currentFilterData = this.templateCommonService.getCurrentFilterData();
    if (currentFilterData && currentFilterData.selectedEntities) {
      previousEntityNames = currentFilterData.selectedEntities.map((e: any) => e.name || e).sort();
    }
    const filterDataSub = this.templateCommonService.filterData$.subscribe((filterData: any) => {
      // Do not auto-trigger API on Enter; only track names and wait for Analyze/Compare
      if (filterData && filterData.selectedEntities) {
        const currentEntityNames = filterData.selectedEntities.map((e: any) => e.name || e).sort();
        previousEntityNames = currentEntityNames;
      }
    });

    // Add subscriptions to array
    this.subscriptions.push(analysisSub, filterDataSub);

    // Load data when component initializes
    this.loadConsumerInsightsData();
  }

  ngOnDestroy(): void {
    console.log('🗑️ Consumer Insights: Destroying component and clearing memory...');
    
    // Unsubscribe from all subscriptions
    this.subscriptions.forEach(sub => sub.unsubscribe());
    
    // Don't clear service-level cache - data should persist across tab switches
    // The service manages the data and tabs subscribe to observables
    
    // Clear all local data references
    this.consumerInsightsResponse = null;
    this.data = null;
    this.satisfactionRadarData = [];
    this.decisionFactorsChartData = [];
    // this.combinedTableData = [];
    this.consumerBehaviorData = null;
    this.purchaseAnalytics = null;
    this.customerPreferences = null;
    this.purchaseFunnel = null;
    this.comparativeInsights = null;
    this.evidenceSources = [];
    this.recommendations = [];
    this.demographicsData = null;
    this.genderChartData = [];
    this.ageChartData = [];
    this.interestChartData = [];
    this.userTypesChartData = [];
    this.additionalDemographics = [];
    
    console.log('✅ Consumer Insights: Component destroyed and memory cleared');
  }

  loadConsumerInsightsData(): void {
    // Check if we already have a response (data might have been loaded before component initialized)
    const currentResponse = this.templateCommonService.getCurrentConsumerInsightsResponse();
    if (currentResponse) {
      console.log('Consumer Insights: Found existing response, processing immediately');
      this.consumerInsightsResponse = currentResponse;
      this.data = currentResponse.structured_data ?? currentResponse;
      this.processData();
      this.cdr.detectChanges();
    } else {
      // No data yet - subscription in ngOnInit() will handle it when data arrives
      console.log('Consumer Insights: No data yet, waiting for subscription to receive data');
    }
  }

  // Method to trigger reload from parent component
  onReload(): void {
    this.templateCommonService.triggerTabReload('consumer-insights', true);
  }

  // Method to retry loading data (used in error state)
  retryLoad(): void {
    this.onReload();
  }

  private processData(): void {
    if (!this.data) return;

    // Handle nested structured_data if present
    let responseData = this.data;
    if (this.data.structured_data) {
      responseData = this.data.structured_data;
    }

    // Extract evidence sources and recommendations
    this.evidenceSources = responseData.evidence_sources || [];
    this.recommendations = responseData.recommendations || [];
    this.comparativeInsights = responseData.comparative_insights || null;
    this.consumerBehaviorData = responseData.consumer_behavior_integration || null;

    // Process brands data from the API structure (radar chart data)
    if (responseData.data && Array.isArray(responseData.data)) {
      this.satisfactionRadarData = responseData.data.map((brand: any) => ({
        name: brand.name,
        series: brand.series || []
      }));

      // Create decision factors chart from first brand's series
      if (responseData.data.length > 0 && responseData.data[0].series) {
        this.decisionFactorsChartData = responseData.data[0].series.map((item: any) => ({
          name: item.name,
          value: item.value
        }));
      }
    }

    // Process table data — disabled while Consumer Insights Summary table is hidden in template
    // if (responseData.table && Array.isArray(responseData.table)) {
    //   this.combinedTableData = responseData.table.map((item: any) => ({
    //     brand: item.brand,
    //     customer_acquisition_rate: item.customer_acquisition_rate || 0,
    //     customer_satisfaction: item.customer_satisfaction || 0,
    //     brand_loyalty: item.brand_loyalty || 0,
    //     average_purchase_value: item.average_purchase_value || 0,
    //     recommendation_score: item.recommendation_score || 0,
    //     market_perception: item.market_perception || 0,
    //     retention_rate: item.retention_rate || 0
    //   }));
    // }

    // Process consumer behavior data
    if (this.consumerBehaviorData) {
      this.purchaseAnalytics = this.consumerBehaviorData.purchase_analytics || null;
      this.customerPreferences = this.consumerBehaviorData.customer_preferences || null;
      this.purchaseFunnel = this.consumerBehaviorData.purchase_funnel || null;
    }

    // Process demographics data
    this.processDemographicsData(responseData);

    this.cdr.markForCheck();
  }

  private processDemographicsData(responseData: any): void {
    let demographicsToProcess = null;
    
    // Check for demographics in new structure (under structured_data.demographics)
    if (responseData.demographics && typeof responseData.demographics === 'object') {
      // New structure: demographics is an object with brand names as keys
      const brandNames = Object.keys(responseData.demographics);
      if (brandNames.length > 0) {
        // Use first brand's demographics (primary brand)
        const primaryBrandName = brandNames[0];
        demographicsToProcess = responseData.demographics[primaryBrandName];
      }
    }
    // Legacy structure: Check if demographics data exists in brands array
    else if (responseData.brands && Array.isArray(responseData.brands) && responseData.brands.length > 0) {
      // Get demographics from first brand (primary brand)
      const primaryBrand = responseData.brands[0];
      if (primaryBrand.demographics) {
        demographicsToProcess = primaryBrand.demographics;
      }
    }
    
    // Process demographics if found
    if (demographicsToProcess) {
      this.demographicsData = demographicsToProcess;
      
      // Process Gender Chart Data
        if (this.demographicsData.gender) {
          this.genderChartData = [];
          if (this.demographicsData.gender.male) {
            this.genderChartData.push({
              name: 'Male',
              value: this.demographicsData.gender.male.percentage,
              absolute: this.demographicsData.gender.male.absolute,
              description: this.removeSurveyedText(this.demographicsData.gender.male.description)
            });
          }
          if (this.demographicsData.gender.female) {
            this.genderChartData.push({
              name: 'Female',
              value: this.demographicsData.gender.female.percentage,
              absolute: this.demographicsData.gender.female.absolute,
              description: this.removeSurveyedText(this.demographicsData.gender.female.description)
            });
          }
        }

        // Process Age Chart Data
        if (this.demographicsData.age) {
          this.ageChartData = [];
          const ageRanges = [
            { key: '20_30', label: '20-30' },
            { key: '31_40', label: '31-40' },
            { key: '41_50', label: '41-50' },
            { key: '51_60', label: '51-60' }
          ];
          ageRanges.forEach(range => {
            const ageData = this.demographicsData.age[range.key];
            if (ageData) {
              this.ageChartData.push({
                name: range.label,
                value: ageData.percentage,
                absolute: ageData.absolute,
                description: this.removeSurveyedText(ageData.description)
              });
            }
          });
        }

        // Process Interest Chart Data
        if (this.demographicsData.interests && this.demographicsData.interests.categories) {
          this.interestChartData = this.demographicsData.interests.categories.map((cat: any) => ({
            name: cat.name,
            value: cat.percentage,
            absolute: cat.absolute,
            description: this.removeSurveyedText(cat.description)
          }));
        }

        // Process User Types Chart Data
        if (this.demographicsData.user_types && this.demographicsData.user_types.types) {
          this.userTypesChartData = this.demographicsData.user_types.types.map((type: any) => ({
            name: type.name,
            value: type.percentage,
            absolute: type.absolute,
            description: this.removeSurveyedText(type.description)
          }));
        }

        // Process Additional Demographics
        if (this.demographicsData.additional_demographics && Array.isArray(this.demographicsData.additional_demographics)) {
          this.additionalDemographics = this.demographicsData.additional_demographics.map((demo: any) => ({
            ...demo,
            data: Array.isArray(demo.data) ? demo.data.map((item: any) => ({
              ...item,
              description: this.removeSurveyedText(item.description || '')
            })) : demo.data
          }));
        }
    }
  }

  // getCombinedTableColumns(): string[] {
  //   return this.combinedTableColumns.map(col => col.key);
  // }

  hasData(): boolean {
    return !!(this.data && (
      (this.data.data && Array.isArray(this.data.data) && this.data.data.length > 0) ||
      (this.data.table && Array.isArray(this.data.table) && this.data.table.length > 0) ||
      this.data.chart
    ));
  }

  // hasCombinedData(): boolean {
  //   return this.combinedTableData.length > 0;
  // }

  hasConsumerBehaviorData(): boolean {
    return this.consumerBehaviorData !== null;
  }

  hasComparativeInsights(): boolean {
    return this.comparativeInsights !== null;
  }

  /** Text for Comparison Summary (yours compared to others). Uses comparison_summary from API or builds from comparative_insights. */
  getComparisonSummaryText(): string {
    if (this.comparativeInsights?.comparison_summary) {
      return this.comparativeInsights.comparison_summary;
    }
    if (!this.comparativeInsights) return '';
    const top = this.comparativeInsights.top_performer;
    const avg = this.comparativeInsights.average_score;
    if (top && avg != null) {
      return `${top} leads on consumer-centric metrics with an average score of ${Math.round(Number(avg))}.`;
    }
    if (top) return `Top performer: ${top}.`;
    return '';
  }

  hasDemographicsData(): boolean {
    return this.demographicsData !== null && (
      this.genderChartData.length > 0 ||
      this.ageChartData.length > 0 ||
      this.interestChartData.length > 0 ||
      this.userTypesChartData.length > 0 ||
      this.additionalDemographics.length > 0
    );
  }

  getTotalSurveyed(): number {
    if (this.demographicsData) {
      return this.demographicsData.gender?.total_surveyed || 
             this.demographicsData.age?.total_surveyed || 
             this.demographicsData.interests?.total_surveyed ||
             this.demographicsData.user_types?.total_surveyed || 0;
    }
    return 0;
  }

  isArray(value: any): boolean {
    return Array.isArray(value);
  }

  removeSurveyedText(text: string): string {
    if (!text) return text;
    return text.replace(/\s*from\s+\d+\s+surveyed\s+people/gi, '').trim();
  }

  // Expose Math for template use
  Math = Math;

  // Get citations for citation-card component (similar to category-analysis)
  getCitations(): any[] {
    if (!this.consumerInsightsResponse) return [];
    
    // Get citations from multiple possible locations
    const citations = this.consumerInsightsResponse.structured_data?.context?.citations || 
                     this.consumerInsightsResponse.structured_data?.evidence_sources ||
                     this.consumerInsightsResponse.context?.citations ||
                     this.evidenceSources ||
                     [];
    
    return citations.length > 0 ? this.convertToCitations(citations) : [];
  }

  // Convert evidence sources to citation format
  private convertToCitations(sources: any[]): any[] {
    return sources.map((source: any) => ({
      title: source.title || source.source || '',
      url: source.url || '',
      snippet: source.snippet || source.description || '',
      source: source.source || source.domain || 'Web search',
      domain: source.domain || source.source || '',
      confidence: source.confidence || source.confidence_score || 0.8,
      type: source.type || 'web'
    }));
  }
}

